import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import NavBar from './components/NavBar';
import HomePage from './pages/HomePage';
import HistoricalGallery from './pages/HistoricalGallery';
import PhotoDetail from './pages/PhotoDetail';
import BeforeAfterCreator from './pages/BeforeAfterCreator';
import MyMemories from './pages/MyMemories';
import QrScanner from './pages/QrScanner';
import Privacy from './pages/Privacy';
import UploadPage from './pages/UploadPage';
import { storageManager } from './utils/storageManager';
import { getAnalytics } from './lib/analytics';

// Main App Component
const App = () => {
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const [currentView, setCurrentView] = useState('home');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [userPhotos, setUserPhotos] = useState([]);
  const [isLoadingMemories, setIsLoadingMemories] = useState(true);
  const [savedGalleryState, setSavedGalleryState] = useState(null);
  
  // Stack per tenere traccia della navigazione
  const [navigationStack, setNavigationStack] = useState(['home']);
  
  if (process.env.NODE_ENV !== "development")
    console.log = () => {};

  // Gestione del tasto indietro del browser/dispositivo
  useEffect(() => {
    const handlePopState = (event) => {
      console.log('🔙 Tasto indietro premuto', event.state);
      
      if (event.state && event.state.view) {
        // Naviga alla vista salvata nello stato
        setCurrentView(event.state.view);
        if (event.state.selectedPhoto) {
          setSelectedPhoto(event.state.selectedPhoto);
        }
        if (event.state.navigationStack) {
          setNavigationStack(event.state.navigationStack);
        }
      } else {
        // Se non c'è stato salvato, torna alla home
        goBackInApp();
      }
    };

    // Aggiungi listener per il tasto indietro
    window.addEventListener('popstate', handlePopState);
    
    // Pusha lo stato iniziale
    const initialState = {
      view: currentView,
      selectedPhoto: selectedPhoto,
      navigationStack: navigationStack
    };
    window.history.replaceState(initialState, '', window.location.pathname);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Funzione per navigare indietro nell'app
  const goBackInApp = () => {
    if (navigationStack.length > 1) {
      const newStack = [...navigationStack];
      newStack.pop(); // Rimuovi la vista corrente
      const previousView = newStack[newStack.length - 1];
      
      setNavigationStack(newStack);
      setCurrentView(previousView);
      
      // Se torniamo indietro da photoDetail, pulisci la foto selezionata
      if (currentView === 'photoDetail' || currentView === 'beforeAfter') {
        setSelectedPhoto(null);
      }
    } else {
      // Se siamo già alla radice, vai alla home
      setCurrentView('home');
      setNavigationStack(['home']);
    }
  };

  // Controllo se è la prima visita
  useEffect(() => {
    const hasAcceptedPrivacy = localStorage.getItem('celtic-druid-privacy-accepted');
    if (hasAcceptedPrivacy === 'true') {
      setIsFirstVisit(false);
      setCurrentView('home');
    } else {
      setIsFirstVisit(true);
      setCurrentView('privacy');
    }
  }, []);

  useEffect(() => {
    const analytics = getAnalytics('celtic-druid');
    console.log('Analytics initialized');
  }, []);

  // Carica i ricordi salvati all'avvio dell'app
  useEffect(() => {
    const loadSavedMemories = async () => {
      try {
        console.log('🔄 Caricamento ricordi salvati...');
        const savedMemories = await storageManager.loadMemories();
        setUserPhotos(savedMemories);
        console.log(`✅ Caricati ${savedMemories.length} ricordi salvati`);
        
        if (savedMemories.length > 0) {
          const stats = await storageManager.getStorageStats();
          if (stats) {
            console.log('📊 Statistiche storage:', {
              ricordi: stats.totalMemories,
              dimensione: `${stats.totalSizeMB} MB`,
              più_recente: new Date(stats.newestMemory).toLocaleDateString(),
              più_vecchio: new Date(stats.oldestMemory).toLocaleDateString()
            });
          }
        }
      } catch (error) {
        console.error('❌ Errore nel caricare i ricordi:', error);
        setUserPhotos([]);
      } finally {
        setIsLoadingMemories(false);
      }
    };

    loadSavedMemories();
  }, []);

  const addMemoryAndSave = async (newMemory) => {
    try {
      await storageManager.saveMemory(newMemory);
      console.log('💾 Ricordo salvato nel storage locale');
      setUserPhotos(prev => [newMemory, ...prev]);
      return true;
    } catch (error) {
      console.error('❌ Errore nel salvare il ricordo:', error);
      setUserPhotos(prev => [newMemory, ...prev]);
      return false;
    }
  };

  const deleteMemoryAndSave = async (memoryId) => {
    try {
      await storageManager.deleteMemory(memoryId);
      setUserPhotos(prev => prev.filter(memory => memory.id !== memoryId));
      console.log('🗑️ Ricordo eliminato dal storage locale');
      return true;
    } catch (error) {
      console.error('❌ Errore nell\'eliminare il ricordo:', error);
      return false;
    }
  };

  const handlePrivacyAccepted = () => {
    localStorage.setItem('celtic-druid-privacy-accepted', 'true');
    setIsFirstVisit(false);
    navigateTo('home');
  };

  // Funzione di navigazione modificata per supportare la cronologia
  const navigateTo = (view, photo = null) => {
    console.log(`🧭 Navigazione da ${currentView} a ${view}`);
    
    // Aggiorna lo stack di navigazione
    const newStack = [...navigationStack, view];
    setNavigationStack(newStack);
    
    // Imposta la vista corrente
    setCurrentView(view);
    
    // Imposta la foto se fornita
    if (photo) {
      setSelectedPhoto(photo);
    }
    
    // Pusha il nuovo stato nella cronologia del browser
    const newState = {
      view: view,
      selectedPhoto: photo || selectedPhoto,
      navigationStack: newStack
    };
    
    window.history.pushState(newState, '', `#${view}`);
  };

  // Render appropriate view
  const renderView = () => {
    switch(currentView) {
      case 'gallery':
        return (
          <HistoricalGallery 
            navigateTo={navigateTo} 
            setSelectedPhoto={setSelectedPhoto}
            savedGalleryState={savedGalleryState}
            setSavedGalleryState={setSavedGalleryState}
          />
        );
      case 'photoDetail':
        return (
          <PhotoDetail 
            photo={selectedPhoto} 
            navigateTo={navigateTo} 
            setUserPhotos={setUserPhotos}
            addMemoryAndSave={addMemoryAndSave}
          />
        );
      case 'beforeAfter':
        return (
          <BeforeAfterCreator 
            photo={selectedPhoto} 
            navigateTo={navigateTo} 
            setUserPhotos={setUserPhotos}
            addMemoryAndSave={addMemoryAndSave}
          />
        );
      case 'myMemories':
        return (
          <MyMemories 
            photos={userPhotos} 
            navigateTo={navigateTo}
            setUserPhotos={setUserPhotos}
            deleteMemoryAndSave={deleteMemoryAndSave}
            isLoadingMemories={isLoadingMemories}
          />
        );
      case 'qrScanner':
        return <QrScanner navigateTo={navigateTo} />;
      case 'upload':
        return <UploadPage navigateTo={navigateTo} />;
      case 'privacy':
        return (
          <Privacy 
            navigateTo={navigateTo} 
            isFirstVisit={isFirstVisit}
            onPrivacyAccepted={handlePrivacyAccepted}
          />
        );
      default:
        return <HomePage navigateTo={navigateTo} />;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-stone-900" style={{ backgroundColor: '#171717', paddingBottom: '60px' }}>
      <Header logo="./logo_header.svg"/>
      <main className="flex-grow">
        {renderView()}
      </main>
      {!(isFirstVisit && currentView === 'privacy') && (
        <NavBar 
          navigateTo={navigateTo} 
          currentView={currentView}
          memoriesCount={userPhotos.length}
        />
      )}
    </div>
  );
};

export default App;